import keyBy from 'lodash/keyBy';
import {
    DxMeasureOption,
    TimeSeriesInterval,
    DxTargetMeasure,
    Validatable,
} from '@/types/analytics/data-explorer.types';
import { EventName, TimeSeriesPeriod } from '@/types/analytics/analytics.types';

type DxMeasureOptions = {
    [key: string]: DxMeasureOption;
};

export const MEASURE_OPTIONS: DxMeasureOptions = {
    TOTAL: {
        name: 'total',
        label: 'Total events',
        description: 'Total number of event interactions that occurred for the selected events or apps',
        group: 'Event metrics',
        disabledTargetTypes: [],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    AVERAGE: {
        name: 'average',
        label: 'Visitor event average',
        description: 'Average number of events that occurred per visitor',
        group: 'Event metrics',
        disabledTargetTypes: [EventName.App],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    AVERAGE_ACCOUNTS: {
        name: 'averageAccounts',
        label: 'Account event average',
        description: 'Average number of events that occurred per account',
        group: 'Event metrics',
        disabledTargetTypes: [EventName.App],
        requiredSubFlags: ['hasAccountLevelAnalyticsEnabled'],
        requiresFlagCheck: false,
    },
    MEDIAN: {
        name: 'median',
        label: 'Median events',
        description: 'Median number of events that occurred per active visitor for the selected events or apps',
        group: 'Event metrics',
        disabledTargetTypes: [EventName.App],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    NUM_MINUTES: {
        name: 'numMinutes',
        label: 'Total time',
        description: 'Total amount of time spent on the selected Pages or apps',
        group: 'Time metrics',
        disabledTargetTypes: [EventName.TrackEvent, EventName.Guide, EventName.Feature],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    AVERAGE_MINUTES_PER_VISITOR: {
        name: 'averageMinutesPerVisitor',
        label: 'Average time per visitor',
        description: 'Average amount of time spent on the selected Pages or apps per visitor',
        group: 'Time metrics',
        disabledTargetTypes: [EventName.TrackEvent, EventName.Guide, EventName.Feature],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    AVERAGE_MINUTES_PER_ACCOUNT: {
        name: 'averageMinutesPerAccount',
        label: 'Average time per account',
        description: 'Average amount of time spent on the selected Pages or apps per account',
        group: 'Time metrics',
        disabledTargetTypes: [EventName.TrackEvent, EventName.Guide, EventName.Feature],
        requiredSubFlags: ['hasAccountLevelAnalyticsEnabled'],
        requiresFlagCheck: false,
    },
    NUM_VISITORS: {
        name: 'numVisitors',
        label: 'Number of unique visitors',
        description: 'Number of unique visitors who used the selected events or apps',
        group: 'Visitor metrics',
        disabledTargetTypes: [],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    NUM_ACCOUNTS: {
        name: 'numAccounts',
        label: 'Number of unique accounts',
        description: 'Number of unique accounts that used the selected events or apps',
        group: 'Account metrics',
        disabledTargetTypes: [],
        requiredSubFlags: ['hasAccountLevelAnalyticsEnabled'],
        requiresFlagCheck: false,
    },
    PERCENT_VISITORS: {
        name: 'percentVisitors',
        label: 'Percent of visitors',
        description: 'Percentage of visitors in your subscription who used the selected events or apps',
        group: 'Visitor metrics',
        disabledTargetTypes: [EventName.App, EventName.Guide],
        requiredSubFlags: [],
        requiresFlagCheck: true,
    },
    PERCENT_ACCOUNTS: {
        name: 'percentAccounts',
        label: 'Percent of accounts',
        description: 'Percentage of accounts in your subscription that used the selected events or apps',
        group: 'Account metrics',
        disabledTargetTypes: [EventName.App, EventName.Guide],
        requiredSubFlags: ['hasAccountLevelAnalyticsEnabled'],
        requiresFlagCheck: true,
    },
    ERROR_CLICKS: {
        name: 'errorClicks',
        label: 'Error clicks',
        description:
            'The number of clicks that generated an error message that occurred for the selected events or apps',
        group: 'Frustration metrics',
        disabledTargetTypes: [EventName.Guide, EventName.TrackEvent],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    RAGE_CLICKS: {
        name: 'rageClicks',
        label: 'Rage clicks',
        description:
            'The number of repetitive clicks on a specific element that occurred for the selected events or apps',
        group: 'Frustration metrics',
        disabledTargetTypes: [EventName.Guide, EventName.TrackEvent],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    U_TURNS: {
        name: 'uTurns',
        label: 'U-turns',
        description:
            'The number of instances a user left a page within seven seconds that occurred for the selected events or apps',
        group: 'Frustration metrics',
        disabledTargetTypes: [EventName.Guide, EventName.TrackEvent, EventName.Feature],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
    DEAD_CLICKS: {
        name: 'deadClicks',
        label: 'Dead clicks',
        description: 'The number of clicks that have no effect that occurred for the selected events or apps',
        group: 'Frustration metrics',
        disabledTargetTypes: [EventName.Guide, EventName.TrackEvent],
        requiredSubFlags: [],
        requiresFlagCheck: false,
    },
};

const measureOptionsByName = keyBy(Object.values(MEASURE_OPTIONS), 'name');

export const getLabelFromTargetGroupMeasure = (measure?: Validatable<DxTargetMeasure>) => {
    if (!measure || !measure.value.name) {
        return '';
    }

    return measureOptionsByName[measure.value.name]?.label;
};

type TimeIntervalOption = {
    name: TimeSeriesPeriod;
    label: string;
    interval: TimeSeriesInterval;
};

export const TIME_INTERVAL_OPTIONS: TimeIntervalOption[] = [
    {
        name: TimeSeriesPeriod.Hourly,
        label: 'Hourly',
        interval: 'hour',
    },
    {
        name: TimeSeriesPeriod.Daily,
        label: 'Daily',
        interval: 'day',
    },
    {
        name: TimeSeriesPeriod.Weekly,
        label: 'Weekly',
        interval: 'week',
    },
    {
        name: TimeSeriesPeriod.Monthly,
        label: 'Monthly',
        interval: 'month',
    },
];
